/**
 * Code to calculate tag columns and their order when displaying a listing with tags in a table
 *
 * For example, listing storables and their tags in the expanded registry view. Applies whether the table
 * used to display the listing is SimpleTable, ExpandedScrollableTable (ScrollableTable), etc.
 *
 * See ExpandedTableTagUtil for example usage. It depends on this to calculate tag columns for
 * ExpandedScrollableTable.
 */
import _ from 'underscore';

import {StorableSummary} from 'constants/SampleConstants';
import {FieldType, SchemaField} from 'schemas/fields/SchemaFieldConstants';
import {TagSchemaSummaryWithFieldsGraphQL} from 'schemas/useTagSchemaSummariesWithFields';
import APIUtil from 'util/APIUtil';

export interface BaseSchema {
  id: string;
  fields: Array<SchemaField>;
}

export interface TagColumnData {
  name: string;
  displayName?: string;
  tagSchemaFieldId?: number;
  fieldType?: FieldType;
}

/**
 * Return all fields of the schemas, sorted by most-to-least frequent schema and increasing field position
 * When mergeBySchemaFieldName is false, find unique fields by id instead of name
 */
export function getSortedSchemaFieldTagColumnsData(
  schemas: Array<BaseSchema>,
  mergeBySchemaFieldName: boolean = true
): Array<TagColumnData> {
  const schemaCounts = _.countBy(schemas, 'id');
  return _.chain(schemas)
    .compact()
    .uniq('id') // unique schemas
    .sortBy((schema) => schemaCounts[schema.id])
    .reverse() // most-to-least frequent
    .map((schema) => schema.fields)
    .flatten()
    .uniq((schemaField) => (mergeBySchemaFieldName ? schemaField.name : schemaField.id))
    .map(({name, displayName, id, fieldType}) => ({name, displayName, tagSchemaFieldId: id, fieldType}))
    .value();
}

/**
 * Return all fields of the schemas, sorted by most-to-least frequent schema and increasing field position
 * When mergeBySchemaFieldName is false, find unique fields by id instead of name
 */
export function getSortedSchemaFieldTagColumnsDataGraphQL(
  schemas: Array<TagSchemaSummaryWithFieldsGraphQL>,
  mergeBySchemaFieldName: boolean = true
): Array<TagColumnData> {
  const schemaCounts = _.countBy(schemas, 'id');
  return _.chain(schemas)
    .compact()
    .uniq('id') // unique schemas
    .sortBy((schema) => schemaCounts[schema.id])
    .reverse() // most-to-least frequent
    .map((schema) => schema.fieldsJson)
    .flatten()
    .uniq((schemaField) => (mergeBySchemaFieldName ? schemaField.name : schemaField.id))
    .map(({name, displayName, id, fieldType}) => ({name, displayName, tagSchemaFieldId: id, fieldType}))
    .value();
}

/**
 * Return all fields of the storables schemas, sorted by most-to-least frequent type, then most-to-least
 * frequent schema, and increasing field position
 */
export function getStorableTagColumnsData(storables: Array<StorableSummary>): Array<TagColumnData> {
  const storableTypeCounts = _.countBy(storables, (storable) => APIUtil.apiIdToType(storable.id));
  const storableTypes = Object.keys(storableTypeCounts);
  return _.chain(storableTypes)
    .sortBy((type) => storableTypeCounts[type])
    .reverse() // most-to-least frequent storable type
    .map((type) => {
      // return sorted schema field names for schemas of that type
      const storableSchemas = _.chain(storables)
        .filter((storable) => type === APIUtil.apiIdToType(storable.id))
        .map((storable) => storable.storableSchema)
        .value();
      return getSortedSchemaFieldTagColumnsData(storableSchemas);
    })
    .flatten()
    .uniq(({name}) => name)
    .value();
}
